import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useSidebar } from '../../../contexts/SidebarContexts';
import Sidebar from '../../../components/Sidebar';
import OnboardingGuide from '../../../components/common/Onboarding';
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';


export default function ProtectedLayout({ children }) {
  const { user, loading, emailVerified } = useAuth();
  const { open } = useSidebar();
  const [redirectToBilling, setRedirectToBilling] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        if (!user) {
          setCheckingSubscription(false);
          return;
        }

        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (!userSnap.exists()) throw new Error('User document not found');
        const userData = userSnap.data();
        const companyId = userData.companyId;

        if (!companyId) throw new Error('No company found for this user');

        const companySnap = await getDoc(doc(db, 'companies', companyId));
        if (!companySnap.exists()) throw new Error('Company document not found');

        const sub = companySnap.data().subscription || {};
        const now = new Date();
        const trialEnds = sub.trialEnds ? new Date(sub.trialEnds) : null;
        const nextBillingDate = sub.nextBillingDate ? new Date(sub.nextBillingDate) : null;
        const status = sub.status || 'payment_due';
        const cancelAtPeriodEnd = sub.cancelAtPeriodEnd || false;

        const trialActive = trialEnds && now <= trialEnds;
        const billingActive = nextBillingDate ? now <= nextBillingDate : true;
        const hasTrialAccess = status === 'trialing' && trialActive;
        const hasPaidAccess = status === 'active' && billingActive;
        const isCancelingButPaid = cancelAtPeriodEnd && billingActive && status !== 'canceled';

        const shouldRedirect =
          !hasTrialAccess &&
          !hasPaidAccess &&
          !isCancelingButPaid &&
          !location.pathname.startsWith('/billing') &&
          !location.pathname.startsWith('/help');

        if (shouldRedirect) {
          alert("Your subscription has expired or is inactive. Please renew to continue using the system.");
          navigate('/billing', { replace: true });
        }
      } catch (error) {
        setSubscriptionError(error.message);
      } finally {
        setCheckingSubscription(false);
      }
    };
  
    checkSubscription();
  }, [user, location.pathname, navigate]);

  if (loading || checkingSubscription) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-500">
        <div>{subscriptionError}</div>
      </div>
    );
  }

  if (!user || !emailVerified) {
    return <Navigate to="/login" replace />;
  }
  const isQueuePage = location.pathname === '/queue-display';
  const isCustPage = location.pathname === '/customer-display';
  return (
    <div className="bg-black min-h-screen flex">
      {/* Hide sidebar on queue page */}
      {!isQueuePage && !isCustPage && <Sidebar />}

      <div
        className={`flex flex-col flex-1 min-h-screen transition-all duration-300 ${
          isQueuePage ? 'lg:ml-0' : open ? 'lg:ml-64' : 'lg:ml-20'
        }`}
      >
        <main className="flex-1 px-5 pt-4 md:pt-6 md:px-6 md:py-6">
          <div className="max-w-full">
            <OnboardingGuide />
            <div className="space-y-4 md:space-y-6">
              {children || <Outlet />}
            </div>
          </div>
        </main>
        <footer className="text-center py-6 text-gray-500 text-sm bg-[#121212] mt-auto">
          &copy; {new Date().getFullYear()} CashMate. Powered by NextCore Technology Hub.
        </footer>
      </div>
    </div>
  );
}
